import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as Yup from 'yup';

// Style/utility
import { Button, ErrorText, H2, P, Ul } from '@styled';
import colors from '@general/colors';
import { emailRegex } from '@general/email';

// Redux dispatch
import {
    // getMemberState,
    // getCurrentAddressState,
    getFundsState,
    dispatchErrorSet,
    dispatchLoadingSet,
    dispatchFoundationDonationInsert,
    dispatchFoundationDonationCartInsert,
    // dispatchFoundationDonationSubscriptionFetch,
    getErrorState,
} from '../../../reducers/FoundationDonationReducer';

import {
    dispatchMemberInsert,
    dispatchMemberUpdate,
    dispatchMemberAddressUpdate,
} from '../../../../../_shared-react/redux/dispatch/memberDispatch';

// Components
import CheckoutForm from '../../sections/CheckoutForm';
import DonorInfo from '../../sections/DonorInfo';
import TributeInfo from '../../sections/TributeInfo';
import { Tribute } from '../../elements/FieldSets';
import FundAmount from '../../elements/FundAmount';
import FundAmountOther from '../../elements/FundAmountOther';
import { FundsInfo } from '../../elements/FundsInfo';

// Helpers
import useMemberFields from '../../../hooks/useMemberFields';
import useViewer from '@hooks/useViewer';
import { getLoginPath } from '../../../../../icsc-app/js/helpers/url.helpers';

const styles = {
    container: {
        backgroundColor: colors.lightGray,
    },
};

const FormColumn = styled.div`
    width: 100%;
    @media (min-width: 768px) {
        width: 50%;
    }
`;

const FoundationDonationForm = ({
    // member,
    error,
    setError,
    setLoading,
    // currentAddress,
    funds,
    // fetchFoundationDonationSubscription,
    insertFoundationDonation,
    insertFoundationDonationCart,
    insertMember,
    updateMember,
    updateMemberAddress,
}) => {
    // State
    const [memberData, setMemberData] = useState();
    const [stripeError, setStripeError] = useState();
    const [view, setView] = useState('donationForm');
    const { viewer } = useViewer();
    const {
        memberId,
        firstName,
        middleName,
        lastName,
        company,
        email,
        address,
        city,
        stateProvince,
        zip,
        countryID,
        setCompany,
        setCountryID,
        setStateProvince,
    } = useMemberFields(memberData);

    // Form data
    const [fund, setFund] = useState('GEN');
    const [fundObject, setFundObject] = useState(null);
    const [amount, setAmount] = useState(0);
    const [amountOther, setAmountOther] = useState('123');
    const [isTribute, setIsTribute] = useState(false);
    const [isRecurring, setIsRecurring] = useState(false);
    const [tributeType, setTributeType] = useState('');
    const [tributary, setTributary] = useState('');

    const scrollTop = () => {
        setView('donationCart');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        // Initialize fundObject
        if (funds.length > 0) {
            setFundObject(funds.find(({ code }) => code === 'GEN'));
        }
    }, [funds]);

    const saveMember = async values => {
        let memberResponse = null;
        const memberValues = {
            first_name: values.firstName,
            middle_name: values.middleName,
            last_name: values.lastName,
            nonmember_company: values.company,
        };

        const addressValues = {
            address: values.address,
            city: values.city,
            state_province: values.stateProvince,
            zip: values.zip,
            country: values.country,
        };

        try {
            if (!viewer) {
                // Member update
                memberResponse = await insertMember({
                    ...memberValues,
                    email: values.email,
                });

                if (!memberResponse) {
                    return;
                }

                memberResponse = await updateMemberAddress(memberResponse.id, 'main', addressValues);
                setMemberData(memberResponse);
                setView('donationCart');
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            // Member update
            memberResponse = await updateMember(viewer.recordId, memberValues);

            if (!memberResponse) {
                return;
            }

            memberResponse = await updateMemberAddress(memberResponse.id, 'main', addressValues);
        } catch (error) {
            setError(error.message);
        }
    };

    const checkout = async (stripe, cardEl) => {
        setLoading(true);
        setError(null);
        setStripeError(null);

        if (isRecurring) {
            await updateMember(viewer?.recordId || memberData.id, {
                foundation_subscription: true,
            });
        }

        const donationResponse = await insertFoundationDonation({
            first_name: firstName,
            last_name: lastName,
            fund,
            amount: amount !== 'other' ? amount : amountOther,
            tributary,
            tribute_type: tributeType,
            company,
            address,
            city,
            state_province: stateProvince,
            country: countryID,
            zip,
            foundation_subscription: isRecurring,
        });

        setLoading(true);

        const paymentData = {
            payment_method: {
                card: cardEl,
                billing_details: {
                    name: `${firstName} ${lastName}`,
                    address: {
                        postal_code: zip,
                    },
                },
            },
        };

        if (isRecurring) {
            paymentData.setup_future_usage = 'off_session';
        }

        const result = await stripe.confirmCardPayment(donationResponse?.payment_intent?.client_secret, paymentData);

        if (result.error) {
            setStripeError(result.error.message);
            setLoading(false);
        } else {
            await insertFoundationDonationCart(donationResponse.cart_id);
            setView('thankYou');
        }
    };

    // Wait if viewer has not been loaded
    if (typeof viewer === 'undefined') {
        return null;
    }

    // Wait if user is logged in, but memberId has not been populated yet
    if (viewer && !memberId) {
        return null;
    }

    return (
        <>
            <Formik
                validateOnMount="true"
                initialValues={{
                    // Foundation Donation
                    fund: 'General Fund',
                    amount: '',
                    amountOther: '',
                    tribute: 'no',
                    tributeType: '',
                    tributary: '',

                    // Member
                    firstName,
                    middleName,
                    lastName,
                    email,
                    company,

                    // Address
                    address,
                    city,
                    stateProvince,
                    zip,
                    country: countryID,
                }}
                validationSchema={Yup.object({
                    fund: Yup.string().required('Foundation is required.'),
                    amount: Yup.mixed().required('Foundation Amount is required.'),
                    amountOther: Yup.string().when('amount', {
                        is: 'other',
                        then: Yup.string()
                            .required('Foundation Amount is required.')
                            .test('minimum-ten', 'Minimum donation is $10.00.', value => {
                                const donationAmount = parseInt(value, 10);
                                return !isNaN(donationAmount) && donationAmount >= 10;
                            }),
                    }),
                    tributeType: Yup.string().when('tribute', {
                        is: 'yes',
                        then: Yup.string().required('Tribute Type is required.'),
                    }),
                    tributary: Yup.string().when('tribute', {
                        is: 'yes',
                        then: Yup.string().required('Tribute Name is required.'),
                    }),

                    firstName: Yup.string().required('First Name is required.'),
                    lastName: Yup.string().required('Last Name is required.'),
                    email: Yup.string()
                        .matches(emailRegex, 'Email is invalid.')
                        .required('Email is required.'),
                    company: Yup.string().required('Company is required.'),

                    address: Yup.string().required('Address is required.'),
                    city: Yup.string().required('City is required.'),
                    zip: Yup.string().required('Zip/Mail Code is required.'),
                    stateProvince: Yup.string().required('State/Province is required.'),
                })}
            >
                {({ values, errors, isValid, dirty, handleChange, setTouched }) => {
                    return (
                        <Form name="Formik">
                            {view === 'donationForm' && (
                                <>
                                    <div className="p-3" style={styles.container}>
                                        <H2 className="mb-3">Welcome{viewer ? `, ${viewer.firstName}` : ''}</H2>
                                    </div>

                                    <FundsInfo
                                        funds={funds}
                                        selectedFund={fund}
                                        onFundChange={(event, updatedFund) => {
                                            handleChange(event);
                                            setFundObject(updatedFund);
                                            setFund(updatedFund.code);
                                        }}
                                    />

                                    <FundAmount
                                        amount={amount}
                                        onAmountChange={event => {
                                            handleChange(event);
                                            setAmount(event.target.value);
                                            setAmountOther('');
                                        }}
                                    />

                                    <div>
                                        <label>
                                            <input type="checkbox" onChange={e => setIsRecurring(e.target.checked)} />
                                            <span className="ml-0.25rem">
                                                Please make this a recurring monthly donation.
                                            </span>
                                        </label>
                                    </div>

                                    {values.amount === 'other' && (
                                        <FundAmountOther
                                            amount={amountOther}
                                            onAmountChange={event => {
                                                handleChange(event);
                                                setAmountOther(event.target.value);
                                            }}
                                        />
                                    )}

                                    <Tribute
                                        isTribute={isTribute}
                                        onIsTributeChange={event => {
                                            handleChange(event);
                                            const updatedIsTribute = event.target.value === 'yes';

                                            setIsTribute(updatedIsTribute);

                                            if (!updatedIsTribute) {
                                                handleChange({
                                                    target: {
                                                        name: 'tributeType',
                                                        value: '',
                                                    },
                                                });

                                                handleChange({
                                                    target: {
                                                        name: 'tributary',
                                                        value: '',
                                                    },
                                                });

                                                setTributeType('');
                                                setTributary('');
                                            }
                                        }}
                                    />

                                    {isTribute && (
                                        <TributeInfo
                                            tributeType={tributeType}
                                            tributeName={tributary}
                                            onTributeTypeChange={event => {
                                                handleChange(event);
                                                setTributeType(event.target.value);
                                            }}
                                            onTributeNameChange={event => {
                                                handleChange(event);
                                                setTributary(event.target.value);
                                            }}
                                        />
                                    )}

                                    <hr className="my-3" />
                                    <legend>Billing Information</legend>

                                    {!viewer && (
                                        <div>
                                            <P className="mb-2">Already have an account?</P>

                                            <div>
                                                <Button as="a" href={getLoginPath()}>
                                                    Log In
                                                </Button>
                                            </div>

                                            <P className="mt-3">
                                                Or
                                                <br />
                                                <br />
                                                Donate as a guest
                                            </P>
                                        </div>
                                    )}

                                    <DonorInfo
                                        member={viewer}
                                        company={company}
                                        selectedCountry={countryID}
                                        onCompanyChange={event => {
                                            handleChange(event);
                                            setCompany(event.target.value);
                                        }}
                                        onCountryChange={event => {
                                            handleChange(event);
                                            handleChange({
                                                target: {
                                                    name: 'stateProvince',
                                                    value: '',
                                                },
                                            });

                                            setCountryID(event.target.value);
                                            setStateProvince('');
                                        }}
                                        onMemberSave={() => {
                                            saveMember(values);
                                        }}
                                    />

                                    <hr className="my-4" />

                                    {error && error.includes('email') && (
                                        <>
                                            <P className="mb-4">
                                                <ErrorText>
                                                    An account already exists for this e-mail address. Please log in to
                                                    continue.
                                                </ErrorText>
                                            </P>
                                        </>
                                    )}

                                    {error && !error.includes('email') && (
                                        <>
                                            <P className="mb-4">
                                                <ErrorText>Attention: {error}</ErrorText>
                                            </P>
                                        </>
                                    )}

                                    <Button
                                        type="button"
                                        onClick={() => {
                                            if (!isValid || !dirty) {
                                                setTouched({
                                                    fund: true,
                                                    amount: true,
                                                    amountOther: true,
                                                    tributeType: true,
                                                    tributary: true,
                                                    firstName: true,
                                                    lastName: true,
                                                    email: true,
                                                    company: true,
                                                    address: true,
                                                    city: true,
                                                    stateProvince: true,
                                                    zip: true,
                                                    country: true,
                                                });

                                                return;
                                            }

                                            if (!viewer) {
                                                saveMember(values);
                                            } else {
                                                scrollTop();
                                            }
                                        }}
                                    >
                                        Review Cart
                                    </Button>
                                </>
                            )}

                            {view === 'donationCart' && (
                                <>
                                    <fieldset className="mt-0">
                                        <legend>{values.firstName}, Review Your Donation</legend>
                                        <table style={styles.container}>
                                            <tbody>
                                                <tr>
                                                    <th>Foundation</th>
                                                    <th>Amount</th>
                                                    <th>Tribute</th>
                                                    <th>Tribute Name</th>
                                                </tr>
                                                <tr>
                                                    <td>{fundObject.name}</td>
                                                    <td>
                                                        {amount === 'other' ? (
                                                            <>${amountOther}.00</>
                                                        ) : (
                                                            <>${values.amount}.00</>
                                                        )}
                                                    </td>

                                                    {tributeType === '' && <td />}
                                                    {tributeType === 'honorarium' && <td>In Honor of</td>}
                                                    {tributeType === 'memoriam' && <td>In Memory of</td>}
                                                    <td>{values.tributary}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </fieldset>

                                    <FormColumn className="align-top d-inline-block">
                                        <fieldset className="mt-0 mb-4">
                                            <legend className="mb-3">Card Details</legend>

                                            <CheckoutForm
                                                fund={fundObject}
                                                firstName={firstName}
                                                lastName={lastName}
                                                isRecurring={isRecurring}
                                                onCheckoutClick={checkout}
                                                onLoadingChange={setLoading}
                                                onErrorChange={setError}
                                                onRecurringChange={setIsRecurring}
                                            />

                                            {stripeError && (
                                                <>
                                                    <P className="mt-2">
                                                        <ErrorText>Attention: {stripeError}</ErrorText>
                                                    </P>
                                                </>
                                            )}

                                            {error && (
                                                <>
                                                    <P className="mt-2">
                                                        <ErrorText>Attention: {error}</ErrorText>
                                                    </P>
                                                </>
                                            )}
                                        </fieldset>
                                    </FormColumn>

                                    <FormColumn className="align-top d-inline-block">
                                        <Ul className="mt-0">
                                            <legend>Billing Information</legend>
                                            <li>
                                                {values.firstName} {values.lastName}
                                            </li>
                                            <li>{values.address}</li>
                                            <li>
                                                {values.city ? `${values.city}, ` : ''}
                                                {values.stateProvince} {values.zip}
                                            </li>
                                            <li>{values.country}</li>
                                        </Ul>
                                    </FormColumn>

                                    <hr className="my-4" />
                                    <Button onClick={() => setView('donationForm')}>Go Back</Button>
                                </>
                            )}

                            {view === 'thankYou' && (
                                <div className="p-3" style={styles.container}>
                                    <H2 className="mb-4">Thank You, {values.firstName}!</H2>
                                    <P>
                                        Thank you so much for your very generous {isRecurring ? 'recurring ' : ''}
                                        donation of{' '}
                                        {values.amount === 'other' ? (
                                            <>${amountOther}.00 </>
                                        ) : (
                                            <>${values.amount}.00 </>
                                        )}
                                        to {fundObject.name}.
                                    </P>

                                    {values.tributary && values.tributeType && (
                                        <P>
                                            {tributeType === 'honorarium' && (
                                                <span>Tribute: In Honor of {values.tributary}.</span>
                                            )}

                                            {tributeType === 'memoriam' && (
                                                <span>Tribute: In Memory of {values.tributary}.</span>
                                            )}
                                        </P>
                                    )}
                                    <P>
                                        Your financial support helps us continue in our mission and the assist those in
                                        our community.
                                    </P>
                                </div>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

FoundationDonationForm.propTypes = {
    error: PropTypes.string,
    setError: PropTypes.func.isRequired,
    setLoading: PropTypes.func.isRequired,
    // member: PropTypes.object,
    // currentAddress: PropTypes.object,
    funds: PropTypes.array.isRequired,
    // fetchFoundationDonationSubscription: PropTypes.func,
    insertFoundationDonation: PropTypes.func,
    insertFoundationDonationCart: PropTypes.func,
    insertMember: PropTypes.func,
    updateMember: PropTypes.func.isRequired,
    updateMemberAddress: PropTypes.func.isRequired,
};

// -----------------------------
// Connect Redux
// -----------------------------

const mapStateToProps = state => ({
    error: getErrorState(state),
    // member: getMemberState(state),
    // currentAddress: getCurrentAddressState(state),
    funds: getFundsState(state),
});

const mapDispatchToProps = dispatch => ({
    setError: error => dispatchErrorSet(error)(dispatch),
    setLoading: loading => dispatchLoadingSet(loading)(dispatch),
    // fetchFoundationDonationSubscription: (...args) => dispatchFoundationDonationSubscriptionFetch(...args)(dispatch),
    insertFoundationDonation: values => dispatchFoundationDonationInsert(values)(dispatch),
    insertFoundationDonationCart: (cartID, values) => dispatchFoundationDonationCartInsert(cartID, values)(dispatch),
    insertMember: (...args) => dispatchMemberInsert(...args)(dispatch),
    updateMember: (...args) => dispatchMemberUpdate(...args)(dispatch),
    updateMemberAddress: (...args) => dispatchMemberAddressUpdate(...args)(dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(FoundationDonationForm);
